/* pdp-video.js
   Video selector for sections/pdp-video.liquid. Added 2026-08-18.

   Swaps the src on the single stage iframe rather than rendering one iframe per
   video — three YouTube embeds on a PDP is a lot of third-party script for
   content most visitors never play.

   Implements the tablist keyboard contract: arrows move between videos, Home
   and End jump to the ends, and only the active control is in the tab order
   (roving tabindex). Loaded only when a section has more than one video. */
(function () {
  'use strict';

  // Builds the real player and drops it in place of the poster button. Called
  // only from a click, so autoplay with sound is permitted here — the browser
  // treats it as user-initiated.
  function loadPlayer(section, url, title) {
    var stage = section.querySelector('.ba-pdp-video__stage');
    if (!stage) return null;
    var iframe = document.createElement('iframe');
    iframe.className = 'ba-pdp-video__iframe';
    iframe.setAttribute('src', url.indexOf('autoplay=') === -1
      ? url + (url.indexOf('?') === -1 ? '?' : '&') + 'autoplay=1'
      : url);
    iframe.setAttribute('title', title || 'Video');
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    stage.innerHTML = '';
    stage.appendChild(iframe);
    return iframe;
  }

  function initFacade(section) {
    var facade = section.querySelector('.ba-pdp-video__facade');
    if (!facade) return;
    facade.addEventListener('click', function () {
      loadPlayer(section, facade.getAttribute('data-video-url'), facade.getAttribute('data-video-title'));
    });
  }

  function initSection(section) {
    initFacade(section);

    var strip = section.querySelector('.ba-pdp-video__strip');
    if (!strip) return;

    var picks = Array.prototype.slice.call(strip.querySelectorAll('.ba-pdp-video__pick'));
    if (picks.length < 2) return;

    function select(index, moveFocus) {
      var pick = picks[index];
      if (!pick) return;
      var url = pick.getAttribute('data-video-url');
      if (!url) return;

      var frame = section.querySelector('.ba-pdp-video__iframe');
      var facade = section.querySelector('.ba-pdp-video__facade');

      // Already the active video: do nothing but move focus. Comparing the
      // iframe src instead would not work — YouTube and Vimeo rewrite their own
      // src once loaded, so it stops matching what we set and we would reload
      // the player mid-playback on every re-click.
      if (pick.classList.contains('is-active')) {
        if (moveFocus) pick.focus();
        return;
      }

      picks.forEach(function (p, i) {
        var on = i === index;
        p.classList.toggle('is-active', on);
        p.setAttribute('aria-selected', on ? 'true' : 'false');
        p.setAttribute('tabindex', on ? '0' : '-1');
      });

      var label = pick.querySelector('.ba-pdp-video__pick-label');
      var title = label ? label.textContent.trim() : '';

      if (frame) {
        // A player is already open, so the visitor has opted in — switch it
        // directly rather than making them press play again.
        if (frame.getAttribute('src') !== url) frame.setAttribute('src', url);
        if (title) frame.setAttribute('title', title);
      } else if (facade) {
        // Still showing the poster: swap the artwork and the pending URL, but
        // do NOT load the player. Selecting a video is not the same as
        // choosing to watch one.
        facade.setAttribute('data-video-url', url);
        if (title) facade.setAttribute('data-video-title', title);
        var poster = pick.getAttribute('data-poster');
        var img = facade.querySelector('.ba-pdp-video__poster');
        if (poster && img) img.setAttribute('src', poster);
      }

      if (moveFocus) pick.focus();
    }

    picks.forEach(function (pick, i) {
      pick.addEventListener('click', function () { select(i, false); });
      pick.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % picks.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + picks.length) % picks.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = picks.length - 1;
        if (next !== null) { e.preventDefault(); select(next, true); }
      });
    });
  }

  function initAll(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll('[data-section-type="pdp-video"]');
    Array.prototype.forEach.call(sections, initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Re-bind when the theme editor re-renders this section.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) initAll(e.target);
  });

  // Exposed for the headless test in DOCS/AUDITS (CLAUDE.md rule 29).
  if (typeof window !== 'undefined') window.baPdpVideoInit = initAll;
})();
